<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";

const store = useStore();
const { t } = useI18n();

// localStorage 키 상수
const HIDDEN_NOTICES_KEY = 'hidden_notices';

// 숨겨진 공지사항 관리
const hiddenNotices = ref(getHiddenNotices());

// localStorage에서 숨겨진 공지사항 목록 가져오기
function getHiddenNotices() {
  try {
    const stored = localStorage.getItem(HIDDEN_NOTICES_KEY);
    if (!stored) return {};
    
    const parsed = JSON.parse(stored);
    const now = Date.now();
    
    // 24시간이 지난 항목들 제거
    const filtered = {};
    Object.keys(parsed).forEach(noticeId => {
      if (now - parsed[noticeId] < 24 * 60 * 60 * 1000) { // 24시간
        filtered[noticeId] = parsed[noticeId];
      }
    });
    
    // 필터링된 데이터를 다시 저장
    localStorage.setItem(HIDDEN_NOTICES_KEY, JSON.stringify(filtered));
    return filtered;
  } catch (error) {
    console.error('localStorage에서 숨겨진 공지사항 로드 실패:', error);
    return {};
  }
}

// 공지사항 숨기기
function hideNotice(noticeId, hide24Hours = false) {
  console.log('공지사항 숨기기:', noticeId, '24시간:', hide24Hours);
  
  if (hide24Hours) {
    const hidden = getHiddenNotices();
    hidden[noticeId] = Date.now();
    
    try {
      localStorage.setItem(HIDDEN_NOTICES_KEY, JSON.stringify(hidden));
      hiddenNotices.value = hidden;
      console.log('24시간 숨김 저장 완료:', noticeId);
    } catch (error) {
      console.error('localStorage 저장 실패:', error);
    }
  } else {
    // 임시 숨김 (새로고침 시 다시 나타남)
    hiddenNotices.value = { ...hiddenNotices.value, [noticeId]: Date.now() };
    console.log('임시 숨김 적용:', noticeId);
  }
}

// 공지사항이 숨겨져 있는지 확인
function isNoticeHidden(noticeId) {
  return Object.prototype.hasOwnProperty.call(hiddenNotices.value, noticeId);
}

// 표시할 공지사항들 (핀된 것 + 숨겨지지 않은 것)
const visibleNotices = computed(() => {
  return store.state.notices
    .filter((notice) => notice.is_pinned)
    .filter((notice) => !isNoticeHidden(notice.id));
});

// 공지사항 닫기
const closeNotice = (event, noticeId) => {
  console.log('공지사항 닫기:', noticeId);
  event.stopPropagation();
  hideNotice(noticeId, false);
};

// 24시간 숨김 체크박스 변경
const onHide24HoursChange = (event, noticeId) => {
  console.log('24시간 숨김 체크:', noticeId, event.target.checked);
  event.stopPropagation();
  
  if (event.target.checked) {
    hideNotice(noticeId, true);
  }
};

onMounted(() => {
  console.log('공지사항 데이터 로딩 시작');
  store.dispatch("fetchNotices", { page: 1, limit: 10 });
});
</script>

<template>
  <div v-if="visibleNotices && visibleNotices.length > 0" class="notices-floating-container">
    <div 
      v-for="(notice, index) in visibleNotices" 
      :key="notice.id"
      class="notice-card"
      :style="{ 
        '--card-index': index,
        '--total-cards': visibleNotices.length 
      }"
    >
      <!-- 카드 상단 컨트롤 -->
      <div class="notice-controls">
        <button 
          class="close-notice-btn" 
          @click="closeNotice($event, notice.id)"
          :title="t('common.buttons.close')"
        >
          ×
        </button>
      </div>

      <!-- 공지사항 헤더 -->
      <div class="notice-header">
        <h3 class="notice-title">{{ notice.title }}</h3>
        <span class="notice-date">
          {{ new Date(notice.created_at).toLocaleDateString() }}
        </span>
      </div>

      <!-- 공지사항 전체 내용 -->
      <div class="notice-content">
        <div v-html="notice.content ? notice.content.replace(/\\n/g, '<br>') : ''"></div>
      </div>

      <!-- 24시간 숨김 체크박스 -->
      <div class="notice-options">
        <label class="hide-24hours-label" @click="$event.stopPropagation()">
          <input 
            type="checkbox" 
            class="hide-24hours-checkbox"
            @change="onHide24HoursChange($event, notice.id)"
          />
          <span class="checkbox-text">{{ t('common.messages.hideFor24Hours') }}</span>
        </label>
      </div>
    </div>
  </div>
</template>

<style scoped>
.notices-floating-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  width: auto;
  max-width: 1000px;
  max-height: 85vh;
  overflow: visible;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  justify-content: center;
  align-content: start;
  pointer-events: none; /* 배경 클릭 허용 */
}

.notice-card {
  pointer-events: auto; /* 카드만 클릭 가능 */
  background-color: #1a1a1a;
  border: 2px solid #444;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  color: #ffffff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  animation: cardAppear 0.4s ease-out calc(var(--card-index) * 0.1s) both;
  max-width: 300px;
}

@keyframes cardAppear {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.notice-card:hover {
  background-color: #2c2c2c;
  border-color: #666;
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
}

/* 카드 상단 컨트롤 */
.notice-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.close-notice-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  color: #ccc;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  backdrop-filter: blur(10px);
}

.close-notice-btn:hover {
  background: rgba(255, 0, 0, 0.2);
  color: #fff;
  transform: scale(1.1);
}

/* 공지사항 헤더 */
.notice-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  margin-top: 8px; /* 상단 버튼 공간 확보 */
  gap: 16px;
}

.notice-title {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  padding-right: 40px; /* close 버튼 공간 확보 */
}

.notice-date {
  font-size: 12px;
  color: #888;
  white-space: nowrap;
  flex-shrink: 0;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
}

/* 공지사항 전체 내용 */
.notice-content {
  font-size: 14px;
  color: #ddd;
  line-height: 1.6;
  flex: 1;
  margin-bottom: 16px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
}

/* 24시간 숨김 옵션 */
.notice-options {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #444;
}

.hide-24hours-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 13px;
  color: #aaa;
  transition: color 0.2s;
}

.hide-24hours-label:hover {
  color: #ccc;
}

.hide-24hours-checkbox {
  margin-right: 8px;
  accent-color: #007bff;
}

.checkbox-text {
  user-select: none;
}

/* 반응형 디자인 */
@media (max-width: 768px) {
  .notices-floating-container {
    width: auto;
    max-height: 90vh;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
  }
  
  .notice-card {
    padding: 16px;
    min-height: 160px;
    max-width: none;
  }
  
  .notice-title {
    font-size: 14px;
    padding-right: 35px;
  }
  
  .notice-content {
    font-size: 13px;
  }

  .close-notice-btn {
    width: 24px;
    height: 24px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .notices-floating-container {
    width: auto;
    max-height: 95vh;
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .notice-card {
    padding: 14px;
    min-height: 140px;
  }

  .notice-title {
    font-size: 13px;
    padding-right: 30px;
  }

  .notice-content {
    font-size: 12px;
  }

  .hide-24hours-label {
    font-size: 12px;
  }
}

/* 스크롤 시 고정 위치 유지 */
.notices-floating-container {
  position: fixed !important;
}

/* 내용 스크롤바 스타일링 */
.notice-content::-webkit-scrollbar {
  width: 4px;
}

.notice-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}

.notice-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
}

.notice-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>
